"use client";

import { useTranslations } from "next-intl";
import type { Review } from "@/lib/reviews/schema";
import { getAvatarUrl } from "@/lib/getAvatarUrl";
import { StarRating } from "@/components/reviews/star-rating";
import { BADGE_ICONS } from "@/components/reviews/badge-icons";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

type ReviewCardProps = {
  review: Review;
};

function formatReviewDate(iso: string): string {
  const tms = Date.parse(iso);
  if (!Number.isFinite(tms)) return "";
  return new Date(tms).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/* The former `dark` prop reached this component from exactly one place —
   ProfileReviewsPanel, which always passed `true` — so the page branch was dead
   and is gone. The surviving branch is on tokens: `border-border` is `#262626`
   in dark (byte-identical to the old `border-neutral-800`) and `bg-sheet/60`
   tracks the panel, as `bg-neutral-900/60` did.
   @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md */
export function ReviewCard({ review }: ReviewCardProps) {
  const t = useTranslations("reviews");
  const isGiven = review.direction === "given";
  const party = isGiven ? review.counterparty : review.reviewer;
  const displayName =
    typeof party?.display_name === "string" && party.display_name.length > 0
      ? party.display_name
      : typeof party?.username === "string" && party.username.length > 0
        ? `@${party.username.replace(/^@/, "")}`
        : t("anonymousReviewer");

  const dateSource = review.order_placed_at ?? review.published_at ?? review.created_at;
  const dateLabel =
    review.order_placed_at != null
      ? t("orderPlacedOn", { date: formatReviewDate(review.order_placed_at) })
      : formatReviewDate(dateSource);

  const metaLabel = isGiven ? t("youReviewed", { name: displayName }) : dateLabel;

  return (
    <article className="space-y-3 rounded-2xl border border-border bg-sheet/60 p-4">
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10">
          <AvatarImage src={getAvatarUrl(party?.avatar_url) ?? undefined} alt={displayName} />
          <AvatarFallback>{displayName.slice(0, 1).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-foreground">{displayName}</p>
          <p className="text-xs text-muted-foreground/70">{metaLabel}</p>
        </div>
        <StarRating value={review.overall_score} readOnly size={14} />
      </div>

      {review.badges.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {review.badges.map((code) => (
            <span
              key={code}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground"
            >
              {BADGE_ICONS[code] ? <span aria-hidden>{BADGE_ICONS[code]}</span> : null}
              {t(`badges.${code}`)}
            </span>
          ))}
        </div>
      ) : null}

      {typeof review.body === "string" && review.body.trim().length > 0 ? (
        <p className="text-sm leading-relaxed text-foreground/90">{review.body.trim()}</p>
      ) : null}
    </article>
  );
}
